import {
  useEffect,
  useRef,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useAtomValue } from 'jotai';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  cookieJar,
  getLoanOfferRoute,
  uiRoutes,
  useOptimizelyFeature,
  UseSubmitApplicationResultT,
} from '@consumer-frontend/nc-api';
import {
  applicationDecisions,
  isRecentlyVerified,
  redirectTo,
} from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { useApplyImmediately } from '../../routing';
import { accountAtom } from '../../store';
import { NcLoadingSpinner } from '../NcLoadingSpinner';

export const NcVerifySigninPage: React.FC = () => {
  const isSigninFeatureEnabled = useOptimizelyFeature('app_redesign_m3_experiment_sign_in_with_password_routing');
  const [searchParams] = useSearchParams();
  const account = useAtomValue(accountAtom);
  const navigate = useNavigate();
  const signinCodeSearchParam = searchParams.get('signinVerificationCode');
  const signinCodeCookie = cookieJar.getSigninVerificationCode();
  const hasSubmitted = useRef(false);
  const [isSigninVerified] = useState(
    () => signinCodeSearchParam && signinCodeCookie && signinCodeSearchParam === signinCodeCookie,
  );
  const { submitApplication, isSubmitApplicationReady } = useApplyImmediately();

  const handleSubmitResult = async (result: UseSubmitApplicationResultT) => {
    const { isIovationPurchaseSuccessful, response } = result;
    const decision = response?.decision;

    if (
      isIovationPurchaseSuccessful
      && response?.status === 201
      && decision
      && applicationDecisions.includes(decision)
    ) {
      redirectTo(getLoanOfferRoute(decision), true);

      return;
    }

    datadogRum.addAction('verifySigninUnsuccessfulApplicationSubmit', { decision, isIovationPurchaseSuccessful });
    redirectTo(uiRoutes.legacyAH.accountSummary, true);
  };

  const handleSubmitError = (error: Error) => {
    datadogRum.addError(error, { context: 'verifySignin' });
    redirectTo(uiRoutes.legacyAH.accountSummary, true);
  };

  /**
   * Handles loan submission and redirection after successful signin,
   * or redirection for unauthorized or not recently verified accounts.
   */
  useEffect(() => {
    if (!isSigninFeatureEnabled) {
      const targetUrl = cookieJar.getAccessToken() ? uiRoutes.legacyAH.accountSummary : uiRoutes.legacyAH.signin;

      redirectTo(targetUrl);

      return;
    }

    if (!isSigninVerified) {
      datadogRum.addAction('verifySigninCodeMismatch', {
        hasCookie: !!signinCodeCookie,
        hasSearchParam: !!signinCodeSearchParam,
      });
      cookieJar.removeSigninVerificationCode();
      redirectTo(uiRoutes.legacyAH.accountSummary, true);

      return;
    }

    // account is hydrated in parent route middleware
    if (!account?.income?.source || !isRecentlyVerified(account?.lastVerifiedAt)) {
      cookieJar.removeSigninVerificationCode();
      navigate(uiRoutes.apply.verifyContactInfo, { replace: true });

      return;
    }

    if (!isSubmitApplicationReady) {
      return;
    }

    if (hasSubmitted.current) {
      return;
    }

    hasSubmitted.current = true;
    cookieJar.removeSigninVerificationCode();

    submitApplication().then(handleSubmitResult).catch(handleSubmitError);
  }, [
    account,
    isSigninFeatureEnabled,
    isSigninVerified,
    isSubmitApplicationReady,
    navigate,
    signinCodeCookie,
    signinCodeSearchParam,
    submitApplication,
  ]);

  return (
    <Container className="nc-loading-page" hasBoxShadow>
      <NcLoadingSpinner />
    </Container>
  );
};
